import { CreatureMechanicsError, creatureActionPrerequisite, validateCreatureInventory } from './creatureAbilities';
import { groupCreatureDamageDetails } from './creatureDamage';
import { AttackRules } from './core/attack';

/** SRD 5.1 turn-scoped traits use the active turn, not a template identity.
 * date_of_change: 2026-09-05
 */
export const creatureTurnKey = (state: any): string => {
  const round = state.round ?? state.currentRound;
  const turn = state.turn ?? state.currentTurn;
  if (!Number.isInteger(round) || round < 1 || !Number.isInteger(turn) || turn < 0) throw new CreatureMechanicsError('combatState', '', 'round/turn', 'an explicit positive round and nonnegative turn index');
  return `${round}:${turn}`;
};

/** Shared by main attacks and opportunity attacks. SRD 5.1 Brute, Surprise
 * Attack and Martial Advantage modify embedded dice, never description text.
 * Callers provide their authoritative spatial relationship predicate.
 * date_of_change: 2026-09-05
 */
export const prepareCreatureWeaponAttack = (actor: any, target: any, action: any, state: any, hasAdjacentAlly: (distanceFeet: number) => boolean): any => {
  if (action?.mechanics?.kind !== 'attack') return action;
  const reason = creatureActionPrerequisite(actor, action);
  if (reason) throw new CreatureMechanicsError(actor.id, action.id, 'equipmentState', reason);
  const effects: any[] = [];
  const extraDamage: any[] = [];
  for (const trait of actor.traits || []) {
    const m = trait.mechanics;
    if (!m || !['extraWeaponDie', 'conditionalDamage'].includes(m.kind) || !m.attackTypes.includes(action.attack.type)) continue;
    if (m.kind === 'conditionalDamage') {
      if (m.allyWithinFeet && !hasAdjacentAlly(m.allyWithinFeet)) continue;
      if (m.surpriseRound !== undefined) {
        if (!Number.isInteger(state.round ?? state.currentRound)) throw new CreatureMechanicsError(actor.id, trait.id, 'combatState.round', 'an explicit round');
        if ((state.round ?? state.currentRound) !== m.surpriseRound || !target.combatFlags?.surprisedBy?.includes(actor.id)) continue;
      }
      if (m.usage && actor.combatFlags?.creatureAbilityUses?.[trait.id] === creatureTurnKey(state)) continue;
    }
    const baseDie = action.attack.damage[0].dice.match(/^\d+d(\d+)/);
    const formula = m.kind === 'extraWeaponDie' ? `${m.count}d${baseDie[1]}` : m.dice;
    extraDamage.push({ dice: formula, type: action.attack.damage[0].type });
    effects.push({ abilityId: trait.id, name: trait.name, formula, usage: m.usage, ruleReference: trait.ruleReference });
  }
  if (!effects.length) return action;
  const damage = [...action.attack.damage, ...extraDamage];
  return { ...action, attack: { ...action.attack, damage }, damage, damageFormula: damage.map(entry => entry.dice).join('+'), creatureEffects: effects };
};

export const creatureOpportunityActions = (actor: any): any[] => {
  validateCreatureInventory(actor, actor.id);
  return actor.actions.filter((action: any) => action.mechanics.kind === 'attack'
    && ['meleeWeapon', 'meleeSpell'].includes(action.attack.type)
    && !creatureActionPrerequisite(actor, action));
};

/** One melee attack, not Multiattack. Criticals multiply dice only; like-type
 * dice from this hit are grouped before resistance rounds down.
 * All faces are supplied by the caller's validated manual/seeded source.
 * SRD 5.1 opportunity attacks and critical hits. date_of_change: 2026-09-05
 */
export const rollCreatureWeaponAttack = (action: any, armorClass: number, rules: AttackRules, mode: {
  advantage: boolean;
  disadvantage: boolean;
  criticalOnHit: boolean;
  attackModifierDice?: Array<{ sides: number; sign: 1 | -1; purpose: string }>;
}, roll: (sides: number, purpose: string) => number): any => {
  const count = mode.advantage !== mode.disadvantage ? 2 : 1;
  const attackRolls = Array.from({ length: count }, () => roll(20, 'attack'));
  const d20 = count === 1 ? attackRolls[0] : mode.advantage ? Math.max(...attackRolls) : Math.min(...attackRolls);
  // Bless/Bane-style dice are part of the same deterministic attack ledger,
  // before hit-gated damage dice. date_of_change: 2026-09-06
  const attackModifierRolls = (mode.attackModifierDice || []).map((entry) => ({
    ...entry,
    face: roll(entry.sides, entry.purpose)
  }));
  const attackBonus = action.attack.toHit + rules.attackRollModifier
    + attackModifierRolls.reduce((sum, entry) => sum + entry.face * entry.sign, 0);
  const hit = !(rules.naturalOneAlwaysMisses && d20 === 1)
    && ((rules.naturalTwentyAlwaysHits && d20 === 20) || d20 + attackBonus >= armorClass);
  const critical = hit && (mode.criticalOnHit || d20 >= rules.criticalHit.threshold);
  const details = hit ? action.attack.damage.map((entry: any, index: number) => {
    const match = entry.dice.match(/^(\d+)d(\d+)(?:\s*([+-])\s*(\d+))?$/i);
    if (!match) throw new CreatureMechanicsError('', action.id, `attack.damage[${index}].dice`, 'a structured dice formula');
    const diceCount = Number(match[1]) * (critical ? rules.criticalHit.damageDiceMultiplier : 1);
    const sides = Number(match[2]);
    if (!Number.isInteger(diceCount) || diceCount < 1 || diceCount > 100 || sides < 2 || sides > 1000) throw new CreatureMechanicsError('', action.id, `attack.damage[${index}].dice`, '1-100 damage dice with 2-1000 sides');
    const rolls = Array.from({ length: diceCount }, () => roll(sides, 'damage'));
    const modifier = (match[4] ? Number(match[4]) * (match[3] === '-' ? -1 : 1) : 0) + (index === 0 ? rules.damageRollModifier : 0);
    return { formula: entry.dice, damageType: entry.type, rolls, modifier, total: Math.max(0, rolls.reduce((sum, face) => sum + face, 0) + modifier) };
  }) : [];
  return { actionId: action.id, actionName: action.name, d20, attackRolls, attackModifierRolls, attackBonus, total: d20 + attackBonus, armorClass, hit, critical, advantage: mode.advantage, disadvantage: mode.disadvantage,
    effects: action.creatureEffects || [], components: groupCreatureDamageDetails(details).map((entry: any, index: number) => ({
      componentId: `${action.id}:${index}`, damageType: entry.damageType, rolledAmount: entry.total, formula: entry.formula, rolls: entry.rolls
    })) };
};
